import { cn } from "../utils/cn";

// ── Icons ─────────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type StepState = "complete" | "current" | "upcoming";
export type StepperVariant = "default" | "numbered" | "dotted";
export type StepperOrientation = "horizontal" | "vertical";

export interface Step {
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: Step[];
  currentStep: number;
  orientation?: StepperOrientation;
  variant?: StepperVariant;
  onStepClick?: (index: number) => void;
}

// ── Style maps ────────────────────────────────────────────────────────────────

const CIRCLE_BASE = "relative flex items-center justify-center rounded-full transition-all duration-200 z-10";

const CIRCLE_STATE: Record<StepState, string> = {
  complete: "bg-brand-purple border-2 border-brand-purple",
  current:  "bg-white dark:bg-surface border-2 border-brand-purple",
  upcoming: "bg-white dark:bg-surface border-2 border-border-strong",
};

const LABEL_STATE: Record<StepState, string> = {
  complete: "text-content-secondary",
  current:  "text-brand-purple font-semibold",
  upcoming: "text-content-tertiary",
};

const DESC_STATE: Record<StepState, string> = {
  complete: "text-content-tertiary",
  current:  "text-content-secondary",
  upcoming: "text-content-tertiary",
};

// ── Step circle ───────────────────────────────────────────────────────────────

interface StepCircleProps {
  idx: number;
  state: StepState;
  variant: StepperVariant;
  clickable: boolean;
  onStepClick?: (i: number) => void;
}

function StepCircle({ idx, state, variant, clickable, onStepClick }: StepCircleProps) {
  const isDotted = variant === "dotted";
  const size = isDotted ? "w-3 h-3" : "w-8 h-8";

  const inner = isDotted ? null : state === "complete" ? (
    <CheckIcon />
  ) : variant === "numbered" ? (
    <span className={cn(
      "text-xs font-bold",
      state === "current" ? "text-brand-purple" : "text-content-tertiary",
    )}>
      {idx + 1}
    </span>
  ) : null;

  const Tag = clickable && state === "complete" ? "button" : "div";

  return (
    <Tag
      {...(Tag === "button"
        ? {
            type: "button" as const,
            onClick: () => onStepClick?.(idx),
            "aria-label": `Go to step ${idx + 1}`,
          }
        : {})}
      aria-current={state === "current" ? "step" : undefined}
      className={cn(
        CIRCLE_BASE,
        size,
        CIRCLE_STATE[state],
        clickable && state === "complete" && "cursor-pointer hover:opacity-80",
      )}
    >
      {/* Pulsing ring for current step */}
      {state === "current" && !isDotted && (
        <span
          aria-hidden="true"
          className="absolute -inset-1 rounded-full border-2 border-brand-purple/30 animate-ping"
        />
      )}
      {inner}
    </Tag>
  );
}

// ── Connector ─────────────────────────────────────────────────────────────────

function Connector({
  orientation,
  filled,
}: {
  orientation: StepperOrientation;
  filled: boolean;
}) {
  const base = cn(
    "transition-colors duration-300",
    filled ? "bg-brand-purple" : "bg-surface-overlay",
  );

  if (orientation === "horizontal") {
    return (
      <div aria-hidden="true" className={cn("flex-1 h-0.5 mx-2", base)} />
    );
  }

  return (
    <div aria-hidden="true" className={cn("w-0.5 flex-1 min-h-8 my-1", base)} />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Stepper({
  steps,
  currentStep,
  orientation = "horizontal",
  variant = "default",
  onStepClick,
}: StepperProps) {
  const getState = (idx: number): StepState =>
    idx < currentStep ? "complete" : idx === currentStep ? "current" : "upcoming";

  if (orientation === "vertical") {
    return (
      <nav aria-label="Progress">
        <div className="flex flex-col">
          {steps.map((step, idx) => {
            const state = getState(idx);
            return (
              <div key={idx} className="flex gap-4">
                {/* Left column: circle + connector */}
                <div className="flex flex-col items-center">
                  <StepCircle
                    idx={idx}
                    state={state}
                    variant={variant}
                    clickable={!!onStepClick}
                    onStepClick={onStepClick}
                  />
                  {idx < steps.length - 1 && (
                    <Connector orientation="vertical" filled={idx < currentStep} />
                  )}
                </div>

                {/* Right column: label + description */}
                <div className={cn("pt-0.5", idx < steps.length - 1 ? "pb-6" : "")}>
                  <p className={cn("text-sm", LABEL_STATE[state])}>{step.label}</p>
                  {step.description && (
                    <p className={cn("text-xs mt-0.5", DESC_STATE[state])}>
                      {step.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    );
  }

  // Horizontal
  return (
    <nav aria-label="Progress">
      <div className="flex items-start">
        {steps.map((step, idx) => {
          const state = getState(idx);
          const isDotted = variant === "dotted";
          return (
            <>
              {/* Step node */}
              <div key={idx} className="flex flex-col items-center shrink-0">
                <StepCircle
                  idx={idx}
                  state={state}
                  variant={variant}
                  clickable={!!onStepClick}
                  onStepClick={onStepClick}
                />
                {!isDotted && (
                  <div className="mt-2 text-center max-w-[80px]">
                    <p className={cn("text-xs", LABEL_STATE[state])}>{step.label}</p>
                    {step.description && (
                      <p className={cn("text-xs mt-0.5", DESC_STATE[state])}>
                        {step.description}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Connector */}
              {idx < steps.length - 1 && (
                <div className={cn("flex-1 flex flex-col items-stretch", isDotted ? "mt-1.5" : "mt-4")}>
                  <Connector orientation="horizontal" filled={idx < currentStep} />
                </div>
              )}
            </>
          );
        })}
      </div>

      {/* Labels below for dotted variant */}
      {variant === "dotted" && (
        <div className="flex items-start mt-3">
          {steps.map((step, idx) => {
            const state = getState(idx);
            return (
              <>
                <div key={idx} className="flex flex-col items-center text-center shrink-0" style={{ minWidth: 60 }}>
                  <p className={cn("text-xs", LABEL_STATE[state])}>{step.label}</p>
                  {step.description && (
                    <p className={cn("text-xs mt-0.5", DESC_STATE[state])}>
                      {step.description}
                    </p>
                  )}
                </div>
                {idx < steps.length - 1 && <div className="flex-1" />}
              </>
            );
          })}
        </div>
      )}
    </nav>
  );
}

Stepper.displayName = "Stepper";
